import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { audienceKey, useAudienceDirectory } from './audienceDirectory';
import { libraryReleaseClient } from './reuseClient';
export const ELIGIBILITY_HELP = 'Release context for the intended output workflow, not blanket approval for future use. Internal only is not eligible for external output. Local or adopted wording needs its own review.';
export function HistoryLink({ itemId }) {
    const [params] = useSearchParams();
    const next = new URLSearchParams(params);
    next.delete('record');
    next.set('history', itemId);
    return /*#__PURE__*/ _jsx(Link, {
        to: `/library?${next}`,
        "data-testid": Testids.libraryHistory.link,
        children: "Release history"
    });
}
function dateTime(ts) {
    return ts ? ts.toDate().toLocaleString() : 'Not recorded';
}
export function ReleaseContext({ context, audiences = [] }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1 text-xs",
        "data-testid": Testids.libraryHistory.context,
        children: [
            /*#__PURE__*/ _jsx("p", {
                "data-testid": Testids.libraryHistory.eligibility,
                children: context?.externalOutputEligibility === 'external_eligible' ? 'External eligible' : 'Internal only'
            }),
            /*#__PURE__*/ _jsx("p", {
                children: ELIGIBILITY_HELP
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Audience:",
                    ' ',
                    context?.audiences?.map((a)=>audiences.find((choice)=>choice.key === audienceKey(a))?.name || (a.kind === 'scope_node' ? 'Internal scope (name unavailable)' : 'Internal group (name unavailable)')).join(', ') || 'Not recorded'
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Origin: released",
                    ' ',
                    context?.itemType === 'evidence' ? 'internal evidence' : 'Library content'
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Restrictions: ",
                    context?.restrictions || 'None recorded'
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "External output restrictions: ",
                    context?.externalOutputRestrictions || 'None recorded'
                ]
            }),
            context?.itemType === 'template' && /*#__PURE__*/ _jsx("p", {
                children: "Templates are starting structures, not proof of accomplishment."
            }),
            context?.correctionReason && /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Correction: ",
                    context.correctionReason
                ]
            }),
            context?.workingVersionId && /*#__PURE__*/ _jsx("p", {
                children: "Origin: reviewed Library working version"
            }),
            context?.supersededByReleaseId && /*#__PURE__*/ _jsx("p", {
                children: "A newer release supersedes this version."
            }),
            context?.withdrawnAt && /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Withdrawn ",
                    dateTime(context.withdrawnAt),
                    ". Reason:",
                    ' ',
                    context.withdrawalReason || 'Not recorded'
                ]
            })
        ]
    });
}
// Keep only the visible bounded page in memory. Rechecks discard text, titles
// and counts before the request; there is no historical query cache to revive.
export function ReleaseHistory({ itemId }) {
    const directory = useAudienceDirectory();
    const [cursor, setCursor] = useState(0);
    const [previous, setPrevious] = useState([]);
    const [refresh, setRefresh] = useState(0);
    const [state, setState] = useState({
        releases: [],
        loading: true,
        error: false
    });
    useEffect(()=>{
        const onFocus = ()=>setRefresh((n)=>n + 1);
        window.addEventListener('focus', onFocus);
        return ()=>window.removeEventListener('focus', onFocus);
    }, []);
    useEffect(()=>{
        const controller = new AbortController();
        setState({
            releases: [],
            loading: true,
            error: false
        });
        void libraryReleaseClient.listLibraryReleaseHistory({
            libraryItemId: itemId,
            beforeVersion: cursor,
            limit: 20
        }, {
            signal: controller.signal
        }).then((result)=>{
            if (!controller.signal.aborted) setState({
                releases: result.releases,
                loading: false,
                error: false
            });
        }).catch((error)=>{
            if (controller.signal.aborted) return;
            const denied = error instanceof ConnectError && [
                Code.NotFound,
                Code.PermissionDenied,
                Code.Unauthenticated
            ].includes(error.code);
            setState({
                releases: [],
                loading: false,
                error: !denied
            });
        });
        return ()=>controller.abort();
    }, [
        itemId,
        cursor,
        refresh
    ]);
    const move = (next)=>{
        setState({
            releases: [],
            loading: true,
            error: false
        });
        setCursor(next);
    };
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-3 rounded-md border p-4",
        "data-testid": Testids.libraryHistory.panel,
        "aria-busy": state.loading,
        children: [
            /*#__PURE__*/ _jsx("h2", {
                className: "text-lg font-medium",
                children: "Release history"
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Only versions you are currently permitted to read appear here. This link remains usable after withdrawal."
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                disabled: state.loading,
                "data-testid": Testids.libraryHistory.refresh,
                onClick: ()=>{
                    setState({
                        releases: [],
                        loading: true,
                        error: false
                    });
                    directory.retry();
                    setRefresh((n)=>n + 1);
                },
                children: "Refresh history"
            }),
            state.loading && /*#__PURE__*/ _jsx("output", {
                className: "animate-pulse rounded border p-3",
                "data-testid": Testids.libraryHistory.loading,
                children: "Loading release history… Slow connection? You can go back while we wait."
            }),
            state.error && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                "data-testid": Testids.libraryHistory.error,
                children: "Could not load release history. Retry with Refresh history."
            }),
            !state.loading && !state.error && state.releases.length === 0 && /*#__PURE__*/ _jsx("p", {
                "data-testid": Testids.library.recordUnavailable,
                children: "No release history is available here. The item may be unreleased, unavailable, or outside your current permissions."
            }),
            state.releases.map((r)=>/*#__PURE__*/ _jsxs("article", {
                    className: "space-y-2 rounded border p-3",
                    "data-testid": Testids.libraryHistory.entry,
                    "data-version": r.version,
                    children: [
                        /*#__PURE__*/ _jsxs("h3", {
                            className: "font-medium",
                            children: [
                                "v",
                                r.version,
                                " — ",
                                r.releasedTitle
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("p", {
                            children: [
                                "Released ",
                                dateTime(r.releasedAt),
                                r.withdrawn ? ' — Withdrawn: prevents new reuse; cannot recall exported files.' : ''
                            ]
                        }),
                        /*#__PURE__*/ _jsx(ReleaseContext, {
                            context: r.releaseContext,
                            audiences: directory.choices
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "whitespace-pre-wrap",
                            "data-testid": Testids.libraryHistory.content,
                            children: r.releasedContent
                        })
                    ]
                }, r.releaseId)),
            previous.length > 0 && /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                disabled: state.loading,
                "data-testid": Testids.libraryHistory.newer,
                onClick: ()=>{
                    move(previous[previous.length - 1]);
                    setPrevious((p)=>p.slice(0, -1));
                },
                children: "Newer versions"
            }),
            state.releases.length === 20 && /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                "data-testid": Testids.libraryHistory.older,
                onClick: ()=>{
                    setPrevious((p)=>[
                            ...p,
                            cursor
                        ]);
                    move(state.releases[state.releases.length - 1].version);
                },
                children: "Older versions"
            })
        ]
    });
}
